import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, Row, Col, Badge, Button, Table, Alert, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faEdit, 
  faTrash, 
  faPlus, 
  faHome,
  faBuilding,
  faMapMarkerAlt,
  faCalendarAlt,
  faEye,
  faSignOutAlt
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { propertyService } from '../../services/propertyService';

const PropertyGroupDetails = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { userCompany, logout } = useAuth();
  
  const [group, setGroup] = useState(null);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchGroupDetails = async () => {
      try {
        setLoading(true);
        const [groupData, unitsData] = await Promise.all([
          propertyService.getPropertyGroupById(groupId),
          propertyService.getUnitsByGroup(groupId)
        ]);
        
        setGroup(groupData);
        setUnits(unitsData);
      } catch (error) {
        console.error('Error fetching group details:', error);
        setError('Failed to load property group details: ' + (error && error.message ? error.message : error));
      } finally {
        setLoading(false);
      }
    };

    if (groupId) {
      fetchGroupDetails();
    }
  }, [groupId]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleDeleteGroup = async () => {
    try {
      setDeleting(true);
      await propertyService.deletePropertyGroup(groupId);
      navigate('/dashboard');
    } catch (error) {
      console.error('Error deleting group:', error);
      setError('Failed to delete property group');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      available: { variant: 'success', text: 'Available' },
      occupied: { variant: 'warning', text: 'Occupied' },
      maintenance: { variant: 'danger', text: 'Maintenance' },
      reserved: { variant: 'info', text: 'Reserved' }
    };
    
    const config = statusConfig[status] || { variant: 'secondary', text: status };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  const getTypeIcon = (type) => {
    const iconMap = {
      society: faBuilding,
      tower: faBuilding,
      community: faBuilding,
      apartment: faHome,
      villa: faHome,
      penthouse: faHome,
      studio: faHome,
      townhouse: faHome
    };
    return iconMap[type] || faHome;
  };

  const getTypeColor = (type) => {
    const colorMap = {
      society: 'primary',
      tower: 'info',
      community: 'success',
      apartment: 'primary',
      villa: 'success',
      penthouse: 'warning',
      studio: 'info',
      townhouse: 'secondary'
    };
    return colorMap[type] || 'secondary';
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid">
        <Alert variant="danger">
          {error}
          <Button 
            variant="outline-danger" 
            className="ms-3" 
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </Button>
        </Alert>
        <pre style={{color: 'red', background: '#fff0f0', padding: 10, borderRadius: 4}}>{error}</pre>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="container-fluid">
        <Alert variant="warning">
          Property group not found
          <Button 
            variant="outline-warning" 
            className="ms-3" 
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </Button>
        </Alert>
      </div>
    );
  }

  const stats = {
    totalUnits: units.length,
    available: units.filter(u => u.status === 'available').length,
    occupied: units.filter(u => u.status === 'occupied').length,
    maintenance: units.filter(u => u.status === 'maintenance').length,
    reserved: units.filter(u => u.status === 'reserved').length
  };

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <Button 
            variant="outline-secondary" 
            onClick={() => navigate('/dashboard')}
            className="me-3"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back
          </Button>
          <div>
            <h2 className="mb-1">{group.name}</h2>
            <p className="text-muted mb-0">
              <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
              {group.location}
            </p>
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button as={Link} to={`/manager/property-groups/${groupId}/edit`} variant="primary">
            <FontAwesomeIcon icon={faEdit} className="me-2" />
            Edit Group
          </Button>
          <Button as={Link} to={`/manager/property-groups/${groupId}/units/new`} variant="success">
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Add Unit
          </Button>
          <Button variant="outline-danger" onClick={() => setShowDeleteModal(true)}>
            <FontAwesomeIcon icon={faTrash} className="me-2" />
            Delete Group
          </Button>
        </div>
      </div>

      {/* Group Information */}
      <Row className="mb-4">
        <Col lg={8}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={getTypeIcon(group.type)} className={`text-${getTypeColor(group.type)} me-2`} />
                Property Group Details
              </h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label text-muted small">Group Name</label>
                    <p className="mb-0 fw-bold">{group.name}</p>
                  </div>
                  <div className="mb-3">
                    <label className="form-label text-muted small">Type</label>
                    <p className="mb-0">
                      <Badge bg={getTypeColor(group.type)} className="text-capitalize">
                        {group.type}
                      </Badge>
                    </p>
                  </div>
                  <div className="mb-3">
                    <label className="form-label text-muted small">Location</label>
                    <p className="mb-0">{group.location}</p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label text-muted small">Status</label>
                    <p className="mb-0">
                      <Badge bg="success">Active</Badge>
                    </p>
                  </div>
                  <div className="mb-3">
                    <label className="form-label text-muted small">Created</label>
                    <p className="mb-0">
                      <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                      {group.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                    </p>
                  </div>
                  <div className="mb-3">
                    <label className="form-label text-muted small">Last Updated</label>
                    <p className="mb-0">
                      <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                      {group.updatedAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                    </p>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">Units Summary</h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <span>Total Units</span>
                  <Badge bg="primary">{stats.totalUnits}</Badge>
                </div>
              </div>
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <span>Available</span>
                  <Badge bg="success">{stats.available}</Badge>
                </div>
              </div>
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <span>Occupied</span>
                  <Badge bg="warning">{stats.occupied}</Badge>
                </div>
              </div>
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <span>Maintenance</span>
                  <Badge bg="danger">{stats.maintenance}</Badge>
                </div>
              </div>
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <span>Reserved</span>
                  <Badge bg="info">{stats.reserved}</Badge>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Units List */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-transparent border-0">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Property Units</h5>
            <Button as={Link} to={`/manager/property-groups/${groupId}/units/new`} variant="primary" size="sm">
              <FontAwesomeIcon icon={faPlus} className="me-2" />
              Add Unit
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {units.length === 0 ? (
            <div className="text-center py-4">
              <FontAwesomeIcon icon={faHome} className="text-muted mb-3" size="3x" />
              <h5>No units found</h5>
              <p className="text-muted">This property group doesn't have any units yet.</p>
              <Button as={Link} to={`/manager/property-groups/${groupId}/units/new`} variant="primary">
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Add First Unit
              </Button>
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Unit Number</th>
                  <th>Type</th>
                  <th>Bedrooms</th>
                  <th>Bathrooms</th>
                  <th>Area (sq ft)</th>
                  <th>Floor</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {units.map((unit) => (
                  <tr key={unit.id}>
                    <td>
                      <strong>{unit.unitNumber}</strong>
                    </td>
                    <td>
                      <Badge bg={getTypeColor(unit.unitType)} className="text-capitalize">
                        {unit.unitType}
                      </Badge>
                    </td>
                    <td>{unit.bedrooms || '-'}</td>
                    <td>{unit.bathrooms || '-'}</td>
                    <td>{unit.area ? `${unit.area} sq ft` : '-'}</td>
                    <td>{unit.floor || '-'}</td>
                    <td>{getStatusBadge(unit.status)}</td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button 
                          as={Link} 
                          to={`/manager/units/${unit.id}`} 
                          variant="outline-primary" 
                          size="sm"
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </Button>
                        <Button 
                          as={Link} 
                          to={`/manager/units/${unit.id}/edit`} 
                          variant="outline-secondary" 
                          size="sm"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Property Group</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete the property group <strong>"{group.name}"</strong>?</p>
          <p className="text-danger">
            <strong>Warning:</strong> This will also delete all {units.length} units in this group. 
            This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDeleteGroup}
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete Group'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PropertyGroupDetails; 